import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
  forwardRef,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { AssetGroupRegimeDto } from '@assetaccountant/api/models/asset-group-regime.dto';
import { DepreciationBasis } from '@assetaccountant/api/models/depreciation-basis';
import { DepreciationMethodId } from '@assetaccountant/api/models/depreciation-method-id';
import { DepreciationRuleId } from '@assetaccountant/api/models/depreciation-rule-id';
import { DepreciationRuleDto } from '@assetaccountant/api/models/depreciation-rule.dto';
import { RegimeAttributesDto } from '@assetaccountant/api/models/regime-attributes.dto';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { DepreciationApi } from '@assetaccountant/api/services/depreciation.api';
import { AssetTransaction } from '@assetaccountant/assets/models/asset-transaction-api-model';
import { DepreciationMethodSettings } from '@assetaccountant/assets/models/depreciation-method-settings';
import { AbsoluteValidators } from '@assetaccountant/core/directives/forms/absolute.validator';
import {
  DepreciationMethodDtoWithKey,
  DepreciationMethodKey,
  RegimeDepreciationMethodsDtoWithKey,
} from '@assetaccountant/core/models/depreciation-method';
import {
  ruleIsValidForMethod,
  ruleIsValidForPool,
} from '@assetaccountant/core/models/depreciation-rule';
import { getValidRateRequest } from '@assetaccountant/core/models/get-rate-api-model-request';
import { RegimeSettingApiModel } from '@assetaccountant/core/models/regime-setting-api-model';
import {
  DepreciationService,
  RateLookupResult,
} from '@assetaccountant/core/services/depreciation.service';
import { EnabledPool } from '@assetaccountant/core/services/pools.service';
import {
  TypedFormArray,
  TypedFormBuilder,
  TypedFormControl,
  TypedFormGroup,
  emptyValue,
} from '@assetaccountant/core/utils/typed-forms';
import { BehaviorSubject, Observable, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { DepreciationMethodModifierComponent } from '../depreciation-method-modifier/depreciation-method-modifier.component';
import { AutocalcLifeEndDateComponent } from './autocalc-life-end-date/autocalc-life-end-date.component';

export function getDepreciationMethodId(
  depreciationMethodId: DepreciationMethodId | DepreciationMethodKey,
):
  | { depreciationMethodId: DepreciationMethodId; depreciationMethodId2?: undefined }
  | { depreciationMethodId?: undefined; depreciationMethodId2: DepreciationMethodKey };
export function getDepreciationMethodId(
  depreciationMethodId: DepreciationMethodId | DepreciationMethodKey | undefined | null,
):
  | { depreciationMethodId: DepreciationMethodId; depreciationMethodId2?: undefined }
  | { depreciationMethodId?: undefined; depreciationMethodId2: DepreciationMethodKey }
  | { depreciationMethodId?: undefined; depreciationMethodId2?: undefined };
export function getDepreciationMethodId(
  depreciationMethodId: DepreciationMethodId | DepreciationMethodKey | undefined | null,
):
  | { depreciationMethodId: DepreciationMethodId; depreciationMethodId2?: undefined }
  | { depreciationMethodId?: undefined; depreciationMethodId2: DepreciationMethodKey }
  | { depreciationMethodId?: undefined; depreciationMethodId2?: undefined } {
  if (depreciationMethodId == null) {
    return {};
  }
  return !isNaN(+depreciationMethodId)
    ? { depreciationMethodId: +depreciationMethodId, depreciationMethodId2: undefined }
    : {
        depreciationMethodId: undefined,
        depreciationMethodId2: depreciationMethodId.toString() as DepreciationMethodKey,
      };
}

interface DepreciationMethodData {
  regimeId: number;
  regimeName: string;
  depreciationMethodId: DepreciationMethodId | DepreciationMethodKey;
  poolCode?: string | null;
  lookup: RateLookupResult | null;
  life: number;
  rate: number;
  depreciationRules?: Array<{ id: DepreciationRuleId; group?: string }>;
}

export type AssetRegimeFormGroup = TypedFormGroup<{
  regimeId: TypedFormControl<number>;
  regimeName: TypedFormControl<string>;
  depreciationMethodId: TypedFormControl<DepreciationMethodId | DepreciationMethodKey>;
  poolCode?: TypedFormControl<string | null>;
  lookup: TypedFormControl<RateLookupResult | null>;
  life: TypedFormControl<number>;
  rate: TypedFormControl<number>;
  useEndDate: TypedFormControl<boolean>;
  originalDate: TypedFormControl<string>;
  originalCost: TypedFormControl<number>;
  residualValue: TypedFormControl<number>;
  notes: TypedFormControl<string>;
  taxableUse: TypedFormControl<number | null>;
  depreciationRules: TypedFormArray<
    TypedFormControl<{
      id: DepreciationRuleId;
      group?: string;
    }>
  >;
  settings: TypedFormArray<
    TypedFormGroup<{
      settingType: TypedFormControl<number>;
      value: TypedFormControl<boolean>;
    }>
  >;
}>;

export type MethodOrPoolOption =
  | {
      type: 'method';
      name: string;
      value: DepreciationMethodId | DepreciationMethodKey;
      displayOrder: number;
    }
  | {
      type: 'pool';
      name: string;
      value: string;
      displayOrder: number;
    };

type RegimeData = Omit<
  AssetGroupRegimeDto & Partial<RegimeAttributesDto>,
  'registerUniqueKey' | 'depreciationMethodId' | 'assetGroupId'
> & {
  depreciationMethodId?: DepreciationMethodId | DepreciationMethodKey | null;
};

@Component({
  selector: 'app-depreciation-method-selector',
  templateUrl: './depreciation-method-selector.component.html',
  styleUrls: ['./depreciation-method-selector.component.scss'],
  standalone: false,
})
export class DepreciationMethodSelectorComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  readonly DepreciationBasis = DepreciationBasis;
  readonly RegisterPermission = RegisterPermissionType;

  @ViewChildren(DepreciationMethodModifierComponent)
  modifiers: QueryList<DepreciationMethodModifierComponent>;

  @ViewChildren(forwardRef(() => AutocalcLifeEndDateComponent))
  endDates: QueryList<AutocalcLifeEndDateComponent>;

  @Input() register: RegisterDto;
  @Input() showTaxableUse = false;
  @Input() regimeData?: RegimeData[];
  @Input() allowBlankSettings = false;
  @Input() allowAssessmentData = true;
  @Input() hideResidualValue = false;
  @Input() regimeCssClass = '';
  @Input() taxableUse: number | null;
  @Input() singleRegime = false;
  @Input() poolsByRegime: Record<number, EnabledPool[]> = {};

  @Input() formGroup: TypedFormGroup<{
    taxableUse?: TypedFormControl<number | null>;
    regimes: TypedFormArray<AssetRegimeFormGroup>;
  }>;

  @Input() acquisitionDate?: string;
  @Input() lastAssessmentDate?: string | null;
  @Input() isFirstUse: boolean;
  @Input() acquisitionPrice: number;
  @Input() transactions$: Observable<AssetTransaction[]>;
  @Input() defaultMethodOverrides: Array<{
    regimeId: number;
    methodId?: DepreciationMethodId | DepreciationMethodKey | null;
  }>;
  @Input() requiresRegisterPermission: RegisterPermissionType = RegisterPermissionType.UpdateAsset;
  @Input() defaultToUseEndDate = false;

  // Defaults the depreciation settings even if regimeData is empty (like when there is no default for the group)
  @Input() forceDefaultDepreciationSettings = false;

  private firstUseDate$ = new BehaviorSubject('');
  @Input() set firstUseDate(value: string) {
    this.firstUseDate$.next(value);
  }
  get firstUseDate() {
    return this.firstUseDate$.value;
  }

  private effectiveDate$ = new BehaviorSubject('');
  @Input() set effectiveDate(value: string) {
    this.effectiveDate$.next(value);
    this.updateResidualValueValidation();
  }
  get effectiveDate() {
    return this.effectiveDate$.value;
  }

  currentWdv?: number;
  depreciationMethodsByRegime: {
    [regimeId: number]: DepreciationMethodDtoWithKey[];
  } = {};
  depreciationRulesByRegime: {
    [regimeId: number]: DepreciationRuleDto[];
  } = {};
  regimeDepreciationMethods: RegimeDepreciationMethodsDtoWithKey[];
  transactions: AssetTransaction[] = [];

  originalRemainingLifeByRegimeId: Record<number, number | null | undefined> = {};
  remainingLifeByRegimeId: Record<number, number | undefined> = {};

  mergedOptionsByRegime: Record<number, Observable<MethodOrPoolOption[]>> = {};
  methodIdOrPoolCodeByRegime: Record<
    number,
    TypedFormControl<DepreciationMethodId | DepreciationMethodKey | string>
  > = {};

  get formArray() {
    return this.formGroup.controls.regimes;
  }

  get showHeadings() {
    return this.formArray.length > 1;
  }

  get hasTaxableUse() {
    return (
      !!this.regimeDepreciationMethods && this.regimeDepreciationMethods.some(r => r.hasTaxableUse)
    );
  }

  regimeHasTaxableUse(regime: any) {
    return (
      !!this.regimeDepreciationMethods &&
      this.regimeDepreciationMethods.findIndex(
        r => r.regimeId === regime.regimeId && r.hasTaxableUse,
      ) >= 0
    );
  }

  private subscriptions: Subscription[] = [];
  private modifierSubscriptions: Subscription[] = [];

  regimeSettings: {
    [regimeId: number]: RegimeSettingApiModel[];
  } = {};

  constructor(
    private fb: TypedFormBuilder,
    private depreciationApi: DepreciationApi,
    private depreciationService: DepreciationService,
    @Inject(LOCALE_ID) public locale: string,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.regimeData) {
      this.updateForm(this.regimeData ?? []);
    }

    if (changes.taxableUse) {
      this.formArray.controls.forEach(regimeGroup => {
        regimeGroup.controls.taxableUse.setValue(this.taxableUse);
      });
    }

    if (changes.allowBlankSettings) {
      const lifeRateValidators = this.allowBlankSettings
        ? [Validators.min(0.001)]
        : [Validators.min(0.001), Validators.required];
      this.formArray.controls.forEach(fg => {
        fg.controls.life.setValidators(lifeRateValidators);
        fg.controls.rate.setValidators(lifeRateValidators);
      });
    }

    if (changes.acquisitionDate || changes.firstUseDate) {
      this.updateDepreciationMethods();
    }

    if (changes.acquisitionPrice) {
      this.updateResidualValueValidation();
    }

    if (changes.defaultMethods) {
      this.overrideDefaultMethods();
    }
  }

  ngOnInit() {
    const depreciationMethods$ = this.depreciationApi.get().pipe(shareReplay(1));
    const settings$ = depreciationMethods$.pipe(
      switchMap(next =>
        forkJoin(
          next.map(regime => this.depreciationApi.getRegimeSettings({ regimeId: regime.regimeId })),
        ),
      ),
    );

    forkJoin([this.transactions$ || of([]), depreciationMethods$, settings$])
      // Prevent ExpressionChangedAfterItHasBeenCheckedError if it returns immediately
      .pipe(delay(1))
      .subscribe(([transactions, regimeDepreciationMethodsResponse, regimeSettings]) => {
        this.transactions = transactions;

        this.regimeDepreciationMethods =
          regimeDepreciationMethodsResponse as RegimeDepreciationMethodsDtoWithKey[];
        this.updateDepreciationMethods();

        this.regimeDepreciationMethods.forEach(({ regimeId }) => {
          this.regimeSettings[regimeId] = [];
        });
        regimeSettings.forEach(settings => {
          if (settings.length > 0) {
            this.regimeSettings[settings[0].regimeId] = settings;
          }
        });

        this.initForm();
        this.updateResidualValueValidation();

        this.cd.detectChanges();
      });
  }

  ngAfterViewInit() {
    /**
     * Subscribe to changes to modifiers QueryList
     * When there are changes (e.g. new modifier components) recreate modifierSubscriptions
     */
    this.subscriptions.push(
      this.modifiers.changes.subscribe(() => {
        this.modifierSubscriptions.splice(0).forEach(s => s.unsubscribe());
        this.modifiers.forEach(modifier => {
          this.modifierSubscriptions.push(this.subscribeModifier(modifier));
          this.buildMergedOptions(modifier);
        });
      }),
    );
  }

  private buildMergedOptions(modifier: DepreciationMethodModifierComponent) {
    const regimeId = modifier.regimeId;
    const regimeGroup = this.formArray.controls.find(c => c.controls.regimeId.value === regimeId);
    const includePools = !!regimeGroup?.controls.poolCode;
    const pools = includePools ? (this.poolsByRegime?.[regimeId] ?? []) : [];

    this.mergedOptionsByRegime[regimeId] = modifier.filteredDepreciationMethods$.pipe(
      map(methods => {
        const methodOptions: MethodOrPoolOption[] = methods.map(m => ({
          type: 'method' as const,
          name: m.name,
          value: m.id,
          displayOrder: m.displayOrder || 0,
        }));

        const poolOptions: MethodOrPoolOption[] = pools.map(p => ({
          type: 'pool' as const,
          name: p.name,
          value: p.code,
          displayOrder: p.displayOrder || 0,
        }));

        return [...poolOptions, ...methodOptions].sort((a, b) => a.displayOrder - b.displayOrder);
      }),
    );
  }

  subscribeModifier(modifier: DepreciationMethodModifierComponent) {
    return modifier.filteredDepreciationMethods$.subscribe(newMethods => {
      const methods = this.depreciationMethodsByRegime[modifier.regimeId];
      const methodControl = this.methodIdOrPoolCodeByRegime[modifier.regimeId];

      if (!methods || !methodControl) {
        return;
      }

      const oldMethod = methods.find(m => m.id === methodControl.value);
      if (!oldMethod) {
        return;
      }

      // If the oldMethod still exists in the new list
      if (newMethods.some(m => m.id === methodControl.value)) {
        return;
      }

      const newMethodCode = modifier.getNewMethodCode(oldMethod.code);

      const newMethod = newMethods.find(m => m.code === newMethodCode);

      methodControl.setValue(
        newMethod?.id || emptyValue<DepreciationMethodId | DepreciationMethodKey>(),
      );
      methodControl.markAsDirty();

      this.cd.detectChanges();
    });
  }

  overrideDefaultMethods() {
    if (this.defaultMethodOverrides) {
      this.defaultMethodOverrides.forEach(method => {
        const methodControl = this.methodIdOrPoolCodeByRegime[method.regimeId];

        methodControl?.setValue(
          method.methodId ?? emptyValue<DepreciationMethodId | DepreciationMethodKey>(),
          {
            onlySelf: true,
            emitEvent: false,
          },
        );
      });
    }
  }

  ngOnDestroy() {
    while (this.formArray.length) {
      this.formArray.removeAt(0);
    }

    this.subscriptions.forEach(s => s.unsubscribe());
    this.modifierSubscriptions.forEach(s => s.unsubscribe());
  }

  private updateDepreciationMethods() {
    if (!this.regimeDepreciationMethods) {
      return;
    }

    this.regimeDepreciationMethods.forEach(regime => {
      this.depreciationMethodsByRegime[regime.regimeId] = regime.methods
        .slice()
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

      this.depreciationRulesByRegime[regime.regimeId] = regime.depreciationRules;
    });
  }

  private updateForm(regimeData: RegimeData[]) {
    this.formArray.controls.forEach(control => {
      const regimeGroup = control as AssetRegimeFormGroup;
      const regime = regimeData.find(d => d.regimeId === regimeGroup.controls.regimeId.value);
      if (!regime) {
        return;
      }

      this.patchRegimeForm(regimeGroup, regime);
      regimeGroup.patchValue({
        regimeName: regime.regimeName,
        settings: [],
        depreciationRules:
          regime.depreciationRules?.map(dr => ({
            id: +dr.id,
            group: dr.group ?? undefined,
          })) ?? [],
      });
    });

    regimeData.forEach(r => {
      this.methodIdOrPoolCodeByRegime[r.regimeId]?.setValue(
        r.poolCode ||
          r.depreciationMethodId ||
          emptyValue<DepreciationMethodId | DepreciationMethodKey | string>(),
        { emitEvent: true },
      );
    });
  }

  private initForm() {
    // clear controls
    this.formArray.clear();

    for (const regime of this.regimeDepreciationMethods) {
      // For reassessments only add controls for the specified regimes
      if (
        this.singleRegime &&
        this.regimeData?.length &&
        !this.regimeData.some(r => r.regimeId === regime.regimeId)
      ) {
        continue;
      }

      const methodValidators = this.allowBlankSettings ? [] : [Validators.required];
      const lifeRateValidators = this.allowBlankSettings
        ? [Validators.min(0.001)]
        : [Validators.min(0.001), Validators.required];

      const values = this.regimeData?.find(data => data.regimeId === regime.regimeId);

      const defaultMethod =
        (!values &&
          this.forceDefaultDepreciationSettings &&
          this.getDefaultDepreciationSettingsForRegime(regime)) ||
        null;

      const regimeGroup: AssetRegimeFormGroup = this.fb.group({
        regimeId: [regime.regimeId],
        regimeName: [regime.regimeName],
        depreciationMethodId: [
          emptyValue<DepreciationMethodId | DepreciationMethodKey>(),
          methodValidators,
        ],
        lookup: [null],
        life: [emptyValue<number>(), lifeRateValidators],
        rate: [emptyValue<number>(), lifeRateValidators],
        useEndDate: [this.defaultToUseEndDate],
        originalDate: [emptyValue<string>()],
        originalCost: [emptyValue<number>()],
        residualValue: [emptyValue<number>()],
        notes: emptyValue<string>(),
        settings: this.fb.array(
          this.regimeSettings[regime.regimeId].map(x =>
            this.fb.group({ settingType: x.type, value: false }),
          ),
        ),
        taxableUse: this.taxableUse,
        depreciationRules: this.fb.array(
          regime.depreciationRules.map(r => {
            const control = this.fb.control({
              id: r.id,
              group: r.group ?? undefined,
            });

            if (!values?.depreciationRules.some(rule => rule.id === r.id)) {
              control.disable();
            }

            return control;
          }),
        ),
      });

      this.patchRegimeForm(regimeGroup, values, defaultMethod);

      this.formArray.push(regimeGroup);

      const regimePools = this.poolsByRegime?.[regime.regimeId];
      if (regimePools && regimePools.length > 0) {
        const methodIdOrPoolCode = this.fb.control(
          values?.poolCode || regimeGroup.controls.depreciationMethodId.value,
          methodValidators,
        );
        const poolCode = this.fb.control(values?.poolCode || emptyValue<string | null>());

        this.methodIdOrPoolCodeByRegime[regime.regimeId] = methodIdOrPoolCode;

        regimeGroup.addControl('poolCode', poolCode);

        this.subscriptions.push(
          methodIdOrPoolCode.valueChanges.subscribe(v => {
            const pool = regimePools.find(p => p.code === v);
            if (pool) {
              poolCode.setValue(pool.code);
              const noneMethod = this.regimeDepreciationMethods
                .find(rm => rm.regimeId === regime.regimeId)
                ?.methods.find(m => m.code === 'NONE');
              regimeGroup.controls.depreciationMethodId.setValue(
                noneMethod?.id ?? emptyValue<DepreciationMethodId | DepreciationMethodKey>(),
                { emitEvent: true },
              );
            } else {
              poolCode.setValue(null);
              regimeGroup.controls.depreciationMethodId.setValue(v, { emitEvent: true });
            }
          }),
        );
      } else {
        this.methodIdOrPoolCodeByRegime[regime.regimeId] =
          regimeGroup.controls.depreciationMethodId;
      }

      const depreciationRulesById: Record<number, DepreciationRuleDto> = Object.fromEntries(
        regime.depreciationRules.map(rule => [rule.id, rule] as const),
      );

      this.subscriptions.push(
        combineLatest([
          regimeGroup.controls.depreciationMethodId.valueChanges.pipe(
            startWith(regimeGroup.controls.depreciationMethodId.value),
            distinctUntilChanged(),
          ),
          regimeGroup.controls.lookup.valueChanges.pipe(
            startWith(regimeGroup.controls.lookup.value),
            distinctUntilChanged(),
          ),
        ])
          .pipe(delay(1))
          .subscribe(() => this.onMethodOrLookupChange(regimeGroup)),
        combineLatest([
          this.effectiveDate$.pipe(startWith(this.effectiveDate), distinctUntilChanged()),
          this.firstUseDate$.pipe(startWith(this.firstUseDate), distinctUntilChanged()),
          this.methodIdOrPoolCodeByRegime[regime.regimeId].valueChanges.pipe(
            startWith(this.methodIdOrPoolCodeByRegime[regime.regimeId].value),
            distinctUntilChanged(),
          ),
        ])
          .pipe(skip(1), delay(1))
          .subscribe(() => {
            regimeGroup.controls.life.updateValueAndValidity();
            for (const ruleControl of regimeGroup.controls.depreciationRules.controls) {
              const depreciationRule = depreciationRulesById[ruleControl.value.id];

              if (!this.isValidRule(depreciationRule, regimeGroup.value)) {
                ruleControl.disable();
              }
            }
          }),
        ...regimeGroup.controls.depreciationRules.controls.map(currentRule =>
          // When rules are enabled
          currentRule.statusChanges.pipe(filter(() => currentRule.enabled)).subscribe(() => {
            const depreciationRule = depreciationRulesById[currentRule.value.id];

            // Check pool/method is valid - reset if not
            if (!this.isValidRuleForPoolOrMethod(depreciationRule, regimeGroup.value)) {
              this.methodIdOrPoolCodeByRegime[regime.regimeId].setValue(
                emptyValue<DepreciationMethodId | DepreciationMethodKey | string>(),
              );
              this.methodIdOrPoolCodeByRegime[regime.regimeId].markAsUntouched();
            }

            if (currentRule.value.group) {
              // Check for other rules with the same group - disable any that were selected
              regimeGroup.controls.depreciationRules.controls.forEach(rule => {
                if (
                  rule !== currentRule &&
                  rule.value.group === currentRule.value.group &&
                  rule.enabled
                ) {
                  rule.disable();
                }
              });
            }
          }),
        ),
      );

      if (this.lastAssessmentDate && values?.depreciationMethodId) {
        if (this.firstUseDate === this.lastAssessmentDate) {
          this.originalRemainingLifeByRegimeId[regime.regimeId] = values.life;
        } else {
          const request = getValidRateRequest({
            regimeId: regime.regimeId,
            depreciationMethodId: values.depreciationMethodId,
            life: values.life,
            firstUseDate: this.firstUseDate,
            date: this.lastAssessmentDate,
          });

          if (request) {
            this.depreciationService.getRate(request).subscribe(response => {
              this.originalRemainingLifeByRegimeId[regime.regimeId] = response.remainingLife;
            });
          }
        }
      }
    }
    this.overrideDefaultMethods();
  }

  onRemainingLifeChange(regimeGroup: AssetRegimeFormGroup, remainingLife?: number) {
    const { regimeId, depreciationMethodId, life } = regimeGroup.value;
    this.remainingLifeByRegimeId[regimeId] = remainingLife;

    const originalRemainingLife = this.originalRemainingLifeByRegimeId[regimeId];
    const lifeOrRemainingLife = remainingLife ?? life;
    if (!originalRemainingLife || !lifeOrRemainingLife) {
      return;
    }

    const { rate: originalRate, depreciationMethodId: originalMethodId } =
      this.regimeData?.find(d => d.regimeId === regimeId) ?? {};
    if (originalRate && depreciationMethodId === originalMethodId) {
      const settings = this.getDepreciationSettings({
        regimeId,
        depreciationMethodId,
      });

      /**
       * Only applies when:
       * - life is editable
       * - rate is editable
       * - rate is not autocalculated
       */
      if (settings.isLifeEditable && settings.isRateEditable && !settings.autoCalculateRate) {
        if (regimeGroup.controls.lookup.value) {
          return;
        }
        regimeGroup.controls.rate.setValue(
          originalRate * (originalRemainingLife / lifeOrRemainingLife),
        );
      }
    }
  }

  getLookup({
    regimeId,
    depreciationMethodId,
    lookup,
  }: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId' | 'lookup'>) {
    return this.isLifeEditable({ regimeId, depreciationMethodId }) ? lookup : null;
  }

  onMethodOrLookupChange(regimeGroup: AssetRegimeFormGroup) {
    const { regimeId, depreciationMethodId } = regimeGroup.value;
    const depreciationSettings = this.getDepreciationSettings({ regimeId, depreciationMethodId });

    const lookup = this.getLookup(regimeGroup.value);

    const lifeControl = regimeGroup.controls.life;
    this.updateLifeControl(lifeControl, depreciationSettings);

    const rateControl = regimeGroup.controls.rate;
    this.updateRateControl(rateControl, depreciationSettings);

    this.updateResidualValueControl(regimeGroup.controls.residualValue, depreciationSettings);

    this.updateOriginalControls(
      regimeGroup.controls.originalDate,
      regimeGroup.controls.originalCost,
      depreciationSettings,
    );

    if (lookup) {
      // IRD - Aircraft (international passenger) has no life, but does have rates
      if (lookup.life) {
        lifeControl.setValue(lookup.life);
      }
      if (
        lookup.rates &&
        (this.isLifeApplicable({ regimeId, depreciationMethodId }) ||
          this.isRateApplicable({ regimeId, depreciationMethodId }))
      ) {
        const method = this.depreciationMethodsByRegime[regimeId].find(
          dm => dm.id === depreciationMethodId,
        );
        if (method) {
          const rate = lookup.rates.find(r => new RegExp(r.methodCodeRegex).test(method.code));
          if (rate) {
            if (rate.life && this.isLifeApplicable({ regimeId, depreciationMethodId })) {
              lifeControl.setValue(rate.life);
            }
            if (rate.rate && this.isRateApplicable({ regimeId, depreciationMethodId })) {
              rateControl.setValue(rate.rate);
            }
          }
        }
      }

      const notes = [lookup.asset];
      if (lookup.categories.length > 0) {
        notes.push(`Category: ${lookup.categories.map(c => c.name).join(' \u203A ')}`);
      }
      if (lookup.industry) {
        notes.push(`Industry: ${lookup.industry}`);
      }
      notes.push(`Ref: ${lookup.ruling}`);
      const newNotes = notes.join('\n');
      if (regimeGroup.controls.notes.value.indexOf(newNotes) < 0) {
        regimeGroup.controls.notes.setValue(newNotes);
      }
    }
  }

  updateLifeControl(
    lifeControl: TypedFormControl<number | null>,
    depreciationSettings: DepreciationMethodSettings,
  ) {
    if (!depreciationSettings.isLifeApplicable) {
      lifeControl.reset({ value: null, disabled: true });
      return;
    }

    if (!depreciationSettings.isLifeEditable) {
      if (depreciationSettings.defaultLife) {
        lifeControl.setValue(depreciationSettings.defaultLife);
      }
      lifeControl.disable();
      return;
    }

    lifeControl.enable();
    if (depreciationSettings.defaultLife) {
      if (
        !depreciationSettings.lifeValues ||
        !depreciationSettings.lifeValues.find(x => lifeControl.value && +x === +lifeControl.value)
      ) {
        lifeControl.setValue(+depreciationSettings.defaultLife);
      }
    }
  }

  updateRateControl(
    rateControl: TypedFormControl<number | null>,
    depreciationSettings: DepreciationMethodSettings,
  ) {
    if (!depreciationSettings.isRateApplicable) {
      rateControl.reset({ value: null, disabled: true });
      return;
    }

    if (!depreciationSettings.isRateEditable) {
      if (depreciationSettings.defaultRate) {
        rateControl.setValue(depreciationSettings.defaultRate);
      }
      rateControl.disable();
      return;
    }

    rateControl.enable();
    if (rateControl.value && +rateControl.value < 0.01) {
      rateControl.setValue(+rateControl.value * 100);
    }
  }

  updateResidualValueControl(
    residualValueControl: TypedFormControl<number | null>,
    depreciationSettings: DepreciationMethodSettings,
  ) {
    if (depreciationSettings.isResidualValueApplicable) {
      residualValueControl.enable();
    } else {
      residualValueControl.disable();
    }
  }

  updateOriginalControls(
    originalDateControl: UntypedFormControl,
    originalCostControl: UntypedFormControl,
    depreciationSettings: DepreciationMethodSettings,
  ) {
    if (!depreciationSettings.isOriginalDateApplicable) {
      originalDateControl.disable();
    } else {
      originalDateControl.enable();
    }

    if (!depreciationSettings.isOriginalCostApplicable) {
      originalCostControl.disable();
    } else {
      originalCostControl.enable();
    }
  }

  isLifeApplicable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isLifeApplicable;

  isLifeEditable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isLifeEditable;

  getLifeType = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId' | 'lookup' | 'life'>,
  ) => {
    if (!this.isLifeApplicable(method)) {
      return null;
    }
    if (!this.isLifeEditable(method)) {
      return 'ReadOnly';
    }
    if (this.getLookup(method)?.life || this.getLookup(method)?.rates?.some(x => x.life)) {
      return 'Edit';
    }
    if (this.canUseEndDate(method)) {
      return 'EditLifeOrDate';
    }
    return 'Edit';
  };

  isUnitsOfUse(data: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>) {
    const regimeDepreciationMethod = this.regimeDepreciationMethods.find(
      r => r.regimeId === data.regimeId,
    );
    const method =
      regimeDepreciationMethod &&
      regimeDepreciationMethod.methods.find(m => m.id === data.depreciationMethodId);
    return method?.code === 'UU';
  }

  getRemainingLife(
    data: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId' | 'life'>,
  ): number | null {
    if (!this.isLifeApplicable(data) || this.isUnitsOfUse(data) || !data.life) {
      return null;
    }

    return this.remainingLifeByRegimeId[data.regimeId] || null;
  }

  isValidMethod(
    depreciationMethodId: DepreciationMethodId | DepreciationMethodKey,
    appliedRules?: Array<{ id: number }>,
  ) {
    if (appliedRules?.some(rule => !ruleIsValidForMethod(rule.id, depreciationMethodId))) {
      return false;
    }
    return true;
  }

  isValidPool(poolCode: string, appliedRules?: Array<{ id: number }>) {
    if (appliedRules?.some(rule => !ruleIsValidForPool(rule.id, poolCode))) {
      return false;
    }
    return true;
  }

  isValidRule(rule: DepreciationRuleDto, formValue: DepreciationMethodData) {
    return (
      this.isValidRuleForDate(rule) &&
      this.isValidRuleForPoolOrMethod(rule, formValue) &&
      this.isValidRuleForRuleGroup(rule, formValue.depreciationRules)
    );
  }

  isValidRuleForDate(rule: DepreciationRuleDto) {
    if (!this.acquisitionDate || !this.firstUseDate) return true;

    return (
      rule.acquiredFrom <= this.acquisitionDate &&
      this.acquisitionDate < rule.acquiredTo &&
      rule.firstUseFrom <= this.firstUseDate &&
      this.firstUseDate < rule.firstUseTo
    );
  }

  isValidRuleForPoolOrMethod(
    rule: DepreciationRuleDto,
    { poolCode, depreciationMethodId }: DepreciationMethodData,
  ) {
    if (poolCode) {
      if (!ruleIsValidForPool(rule.id, poolCode)) {
        return false;
      }
    } else if (depreciationMethodId) {
      if (!ruleIsValidForMethod(rule.id, depreciationMethodId)) {
        return false;
      }
    }

    return true;
  }

  isValidRuleForRuleGroup(
    rule: DepreciationRuleDto,
    depreciationRules?: Array<{ id: number; group?: string }>,
  ) {
    if (rule.group && depreciationRules?.some(r => r.id !== rule.id && r.group === rule.group)) {
      return false;
    }
    return true;
  }

  isLifeFreeInput = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isLifeFreeInput;
  getLifeValues = (method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>) =>
    this.getDepreciationSettings(method).lifeValues;
  isOriginalDateApplicable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isOriginalDateApplicable;
  isOriginalCostApplicable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isOriginalCostApplicable;
  isRateApplicable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isRateApplicable;
  isRateEditable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isRateEditable;
  isResidualValueApplicable = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): boolean => this.getDepreciationSettings(method).isResidualValueApplicable;
  depreciationBasis = (
    method: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>,
  ): DepreciationBasis => this.getDepreciationSettings(method).basis;

  getDepreciationSettings({
    regimeId,
    depreciationMethodId,
  }: Pick<
    DepreciationMethodData,
    'regimeId' | 'depreciationMethodId'
  >): DepreciationMethodSettings {
    if (!depreciationMethodId) {
      return DepreciationMethodSettings.default();
    }

    return this.depreciationService.getDepreciationSettings(
      this.regimeDepreciationMethods || [],
      +regimeId,
      depreciationMethodId,
    );
  }

  canUseEndDate = (data: Pick<DepreciationMethodData, 'regimeId' | 'depreciationMethodId'>) => {
    if (!this.firstUseDate) {
      return false;
    }

    const settings = this.getDepreciationSettings(data);

    return (
      settings.isLifeApplicable &&
      settings.isLifeEditable &&
      settings.isLifeFreeInput &&
      !settings.isUnitsOfUse
    );
  };

  private getDefaultDepreciationSettingsForRegime(
    regimeDepreciationMethods: RegimeDepreciationMethodsDtoWithKey,
  ) {
    let method = regimeDepreciationMethods.methods.find(m => m.code === 'SL');
    if (method) {
      return {
        depreciationMethodId: method.id,
        life: 4,
        rate: 0.25,
      };
    }

    // AU
    method = regimeDepreciationMethods.methods.find(m => m.code === 'PC');
    if (method) {
      return {
        depreciationMethodId: method.id,
        life: 4,
      };
    }

    // UK tax
    method = regimeDepreciationMethods.methods.find(m => m.code === 'FYA');
    if (method) {
      return {
        depreciationMethodId: method.id,
      };
    }

    // USA tax
    method = regimeDepreciationMethods.methods.find(m => m.code === 'GDS SL MQ');
    if (method) {
      return {
        depreciationMethodId: method.id,
        life: 3,
      };
    }

    method = regimeDepreciationMethods.methods.find(m => m.code === 'NONE');
    if (method) {
      return {
        depreciationMethodId: method.id,
      };
    }

    return null;
  }

  private updateResidualValueValidation() {
    let limit: number | undefined;
    if (this.transactions && this.effectiveDate) {
      for (
        let i = 0;
        i < this.transactions.length && this.transactions[i].date < this.effectiveDate;
        i++
      ) {
        if (this.transactions[i].writtenDownValue != null) {
          limit = this.transactions[i].writtenDownValue;
        }
      }
      this.currentWdv = limit;
    }

    if (!limit && this.acquisitionPrice) {
      limit = this.acquisitionPrice;
    }

    this.formArray.controls.forEach(regimeGroup => {
      regimeGroup.controls.residualValue.setValidators([
        AbsoluteValidators.max(limit || Number.MAX_SAFE_INTEGER),
        AbsoluteValidators.sameSign(this.acquisitionPrice),
      ]);
      regimeGroup.controls.residualValue.updateValueAndValidity();
    });
  }

  private patchRegimeForm(
    regimeGroup: AssetRegimeFormGroup,
    data: RegimeData | null | undefined,
    defaultMethod?: {
      depreciationMethodId?: DepreciationMethodId | DepreciationMethodKey;
      life?: number;
      rate?: number;
    } | null,
  ): void {
    regimeGroup.patchValue({
      depreciationMethodId:
        data?.depreciationMethodId ||
        defaultMethod?.depreciationMethodId ||
        emptyValue<DepreciationMethodId | DepreciationMethodKey>(),
      lookup: null,
      life: (data?.life && +data.life) || defaultMethod?.life || emptyValue<number>(),
      rate: (data?.rate && +data.rate) || defaultMethod?.rate || emptyValue<number>(),
      useEndDate: this.defaultToUseEndDate,
      originalDate: data?.originalDate || emptyValue<string>(),
      originalCost: (data?.originalCost && +data.originalCost) || emptyValue<number>(),
      residualValue: (data?.residualValue && +data.residualValue) || emptyValue<number>(),
      notes: '',
      taxableUse: this.taxableUse,
    });
  }
}
